import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    type ListAggregationConfig,
    type ListEntryRequirement,
    compileListAggregation,
    decompileListAggregation,
} from '../condition-mapper';
import type { DataCriticality, FieldCondition } from '../product-requirements-config.dto';

const CRITICALITIES: DataCriticality[] = ['mandatory', 'important', 'optional', 'irrelevant'];

type Mode = 'per_entry' | ListEntryRequirement;

interface Props {
    /** The assignment's criticalityWhen — null or a parent-aggregation template. */
    value: FieldCondition | null;
    /** The LIST parent's own code (the aggregation iterates its own entries). */
    ownCode: string;
    onChange: (value: FieldCondition | null) => void;
    disabled?: boolean;
}

const DEFAULT_CONFIG: ListAggregationConfig = {
    requirement: 'at_least_one',
    satisfied: 'optional',
    missing: 'important',
};

/**
 * PM-facing "how strict is this list" selector for a LIST parent's assignment:
 * judge every entry on its own (no rule), or roll the list up so that one /
 * every complete entry satisfies it (compiles to the one-operand `some`/`all`
 * criticalityWhen template — see condition-dsl.md, parent-level aggregation).
 * Callers only render this when the current rule is null or decompiles to the
 * template; anything else goes to the generic rules editor.
 */
export default function ListEntryRequirementEditor({ value, ownCode, onChange, disabled }: Props) {
    const { t } = useTranslation();
    const config = decompileListAggregation(value, ownCode);
    const mode: Mode = config?.requirement ?? 'per_entry';

    function emit(next: Mode, base: ListAggregationConfig) {
        if (next === 'per_entry') onChange(null);
        else onChange(compileListAggregation({ ...base, requirement: next }, ownCode));
    }

    return (
        <Stack spacing={1.5}>
            <FormControl size="small" sx={{ maxWidth: 360 }} disabled={disabled}>
                <InputLabel>{t('Entry requirement')}</InputLabel>
                <Select
                    label={t('Entry requirement')}
                    value={mode}
                    onChange={(e) => emit(e.target.value as Mode, config ?? DEFAULT_CONFIG)}
                >
                    <MenuItem value="per_entry">{t('Judge each entry on its own')}</MenuItem>
                    <MenuItem value="at_least_one">{t('At least one entry must be complete')}</MenuItem>
                    <MenuItem value="every">{t('Every entry must be complete')}</MenuItem>
                </Select>
            </FormControl>

            {config && (
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 190 }} disabled={disabled}>
                        <InputLabel>{t('Until satisfied, count as')}</InputLabel>
                        <Select
                            label={t('Until satisfied, count as')}
                            value={config.missing}
                            onChange={(e) =>
                                onChange(
                                    compileListAggregation(
                                        { ...config, missing: e.target.value as DataCriticality },
                                        ownCode,
                                    ),
                                )
                            }
                        >
                            {CRITICALITIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {t(c)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 190 }} disabled={disabled}>
                        <InputLabel>{t('Once satisfied, count as')}</InputLabel>
                        <Select
                            label={t('Once satisfied, count as')}
                            value={config.satisfied}
                            onChange={(e) =>
                                onChange(
                                    compileListAggregation(
                                        { ...config, satisfied: e.target.value as DataCriticality },
                                        ownCode,
                                    ),
                                )
                            }
                        >
                            {CRITICALITIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {t(c)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                        {t('An entry is complete when all its required sub-fields are filled.')}
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
}
